import { useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LabelList, CartesianGrid, XAxis, YAxis, Tooltip as ChartTooltip,
} from 'recharts';
import { demoTheme } from '../../theme';
import { RaporHatasi } from '../rapor-ekrani/RaporHatasi';
import { paraMetni, tarihMetni } from '../rapor-ekrani/bicim';
import { useRaporSorgu } from '../rapor-ekrani/useRaporSorgu';
import { useDarEkran } from '../rapor-ekrani/useDarEkran';
import type { PanelEkranKaynagi } from '../../types/panelConfig';
import { Kart, Lejant, RENK } from './gorsel';
import { CINS_ADI, zamanKovalari, type HamZamanSatiri, type Taraf } from './tipler';

interface Props {
  kaynak: PanelEkranKaynagi | undefined;
  bas: string;
  bit: string;
  taraf: Taraf;
  /** Bir gün çubuğuna basınca: dönem o güne iner (ekran saatlere göre çizer). */
  onGunSec: (gun: string) => void;
}

const LEJANT = [
  { ad: CINS_ADI.K, renk: RENK.K.yazi },
  { ad: CINS_ADI.E, renk: RENK.E.yazi },
  { ad: CINS_ADI['-'], renk: RENK['-'].yazi },
];

/**
 * Çubuk üstü etiket ve eksen: bin TL, "12 B". Sıfır etiket yazmaz. Boşluk BÖLÜNMEZ boşluktur: recharts
 * etiketi çubuk genişliğine göre boşluktan kırıyordu (390 px'te "107" üstte, "B" altta; ölçüldü).
 */
const binMetni = (v: unknown) => {
  const n = Number(v);
  return n ? `${Math.round(n / 1000)}\u00a0B` : '';
};
/** Bu sayıdan çok çubukta üst etiketler yığılır (31 günlük ayda okunmuyor): yalnız eksen kalır. */
const ETIKET_SINIRI = 16;
const ETIKET_SINIRI_TELEFON = 8;

/**
 * S340: günlere göre ciro, Bayan / Erkek / Kabinsiz yığılı çubuk. Tek gün seçiliyse saatlere göre
 * (`@KIRILIM` = 'saat'; saat adisyonun kapandığı saattir). Çizim recharts (`satis-analiz/ZamanBlok` ile
 * aynı kalıp): çubuğa basmak fareye özgü, klavye ve ekran okuyucu için aynı günler düğme listesinde.
 */
export function CiroCubuklari({ kaynak, bas, bit, taraf, onGunSec }: Props) {
  const tekGun = bas === bit;
  const telefon = useDarEkran();
  const q = useRaporSorgu<HamZamanSatiri>(kaynak, { '@BASTARIH': bas, '@BITTARIH': bit, '@KIRILIM': tekGun ? 'saat' : 'gun' });
  const kovalar = useMemo(() => zamanKovalari(q.data ?? [], bas, bit, taraf), [q.data, bas, bit, taraf]);
  const gunSec = (i: number) => {
    const g = kovalar[i]?.gun;
    if (g) onGunSec(g);
  };
  const cubuk = (alan: 'bayan' | 'erkek' | 'kabinsiz', ad: string, renk: string, ust = false) => (
    <Bar
      dataKey={alan}
      name={ad}
      stackId="ciro"
      fill={renk}
      radius={ust ? [3, 3, 0, 0] : undefined}
      isAnimationActive={false}
      cursor={tekGun ? undefined : 'pointer'}
      onClick={tekGun ? undefined : (_veri, i) => gunSec(i)}
    >
      {ust && kovalar.length <= (telefon ? ETIKET_SINIRI_TELEFON : ETIKET_SINIRI) && (
        <LabelList dataKey="toplam" position="top" formatter={binMetni} style={{ fontSize: 11, fill: demoTheme.textMuted }} />
      )}
    </Bar>
  );

  return (
    <Kart
      testid="hamam-zaman"
      baslik={tekGun ? `Saatlere göre ciro (${tarihMetni(bas)})` : 'Günlere göre ciro'}
      araclar={<Lejant ogeler={LEJANT} />}
    >
      {!kaynak ? (
        <RaporHatasi testid="hamam-zaman-kaynaksiz" mesaj="Bu firmada günlere göre ciro kaynağı tanımlı değil." />
      ) : q.isError ? (
        <RaporHatasi testid="hamam-zaman-hata" mesaj="Günlere göre ciro yüklenemedi." onTekrar={() => q.refetch()} />
      ) : q.isLoading ? (
        <div className="skeleton h-[220px] rounded" data-testid="hamam-zaman-iskelet" />
      ) : kovalar.length === 0 ? (
        <p className="m-0 py-6 text-center text-[13px] text-ink-500" data-testid="hamam-zaman-bos">Bu aralıkta adisyon yok</p>
      ) : (
        <>
          <div
            className="h-[220px] w-full min-w-0"
            data-testid="hamam-zaman-grafik"
            aria-busy={q.isFetching}
            style={q.isFetching ? { opacity: 0.55 } : undefined}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={kovalar} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke={demoTheme.border} vertical={false} />
                <XAxis
                  dataKey="etiket"
                  tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                  axisLine={{ stroke: demoTheme.border }}
                  tickLine={false}
                  minTickGap={12}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tickFormatter={(v) => binMetni(v) || '0'}
                  tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                  axisLine={false}
                  tickLine={false}
                  width={44}
                />
                <ChartTooltip formatter={(v) => paraMetni(Number(v))} labelFormatter={(l) => String(l)} />
                {/* Yığın sırası alttan üste: Bayan, Erkek, Kabinsiz (demodaki sütunla aynı). */}
                {cubuk('bayan', CINS_ADI.K, RENK.K.yazi)}
                {cubuk('erkek', CINS_ADI.E, RENK.E.yazi)}
                {cubuk('kabinsiz', CINS_ADI['-'], RENK['-'].yazi, true)}
              </BarChart>
            </ResponsiveContainer>
          </div>
          {!tekGun && (
            <ul
              data-testid="hamam-gun-listesi"
              aria-label="Güne in"
              className="m-0 p-0 list-none sr-only focus-within:not-sr-only focus-within:mt-2 focus-within:flex focus-within:flex-wrap focus-within:gap-1"
            >
              {kovalar.map((k) => k.gun && (
                <li key={k.gun}>
                  <button
                    type="button"
                    data-testid={`hamam-gun-${k.gun}`}
                    aria-label={`${tarihMetni(k.gun)} gününe in, ciro ${paraMetni(k.toplam)}`}
                    onClick={() => onGunSec(k.gun!)}
                    className="focus-ring text-[11px] px-1.5 py-0.5 rounded border border-border text-ink-700"
                  >
                    {k.etiket}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="m-0 mt-1.5 text-[13px] text-ink-500" data-testid="hamam-zaman-not">
            {tekGun
              ? 'Saat, adisyonun kapandığı saattir. Tutarlar bin TL (B).'
              : 'Bir güne basınca sayfa o güne iner ve saatlere göre gösterir. Tutarlar bin TL (B).'}
          </p>
        </>
      )}
    </Kart>
  );
}
